"use client";

import React, { Fragment, useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { Divider } from "staysure-component-library";

import withBrand from "@/components/with-brand";
import { GOLF_YEAR_PRICE } from "@/config/app-constant";
import { usePolicyExtrasAction } from "@/providers/policy-extras-action-provider";
import { AcoItems } from "@/schema/renewal-page/middle-panel.schema";
import { RootState } from "@/store/root-reducer";
import { OptionalExtra } from "@/types/OptionalExtraType";

import PolicyExtrasOption from "./policy-extras-option";

type RenewalEditPolicyExtrasMiddlePanelProps = {
  editPolicyHeaderMiddlePanelData: {
    addedButtonLabel: string;
    addButtonLabel: string;
    removeButtonLabel: string;
    moreInfoLabel: string;
    backToRenewalsLabel: string;
    chooseLabel: string;
    acoItems: Array<AcoItems>;
    tdeModalTitle?: string;
    tdeModalCopy?: string;
    tdeModalConfirmBtnLabel?: string;
    tdeModalCancelBtnLabel?: string;
  };
  brand?: "staysure" | "avanti";
};

type OptionalExtrasItemType = {
  code: string;
  title: string;
  description: string;
  sellPrice: number;
  pclPrice: number | null | undefined;
  pclTotalCost: number | null | undefined;
  selected: boolean;
  state: string;
  actionLabel: string;
  isGolfEquipment?: boolean;
  golfSelectKey?: string;
  drawerContent: string;
};

function RenewalEditPolicyExtrasMiddlePanel({
  editPolicyHeaderMiddlePanelData,
  brand,
}: RenewalEditPolicyExtrasMiddlePanelProps) {
  const { attributes } = useSelector((state: RootState) => state.quote);
  const { priceResult } = attributes;
  // optional extras for display purpose
  const [optionalExtrasDisplayItems, setOptionalExtrasDisplayItems] =
    React.useState<OptionalExtrasItemType[]>([]);
  const {
    optionalExtrasWithModifiedState,
    setOptionalExtrasWithModifiedState,
    handleOptionalExtrasStateChange,
    isGolfModification,
    setIsGolfModification,
    onChangeGolfCoverTravelersCount,
  } = usePolicyExtrasAction();

  useEffect(() => {
    if (priceResult && isGolfModification) {
      const golfCover = priceResult?.optionalExtras.find(
        (extra) => extra.code === "GOLF_EQUIPMENT"
      );
      if (golfCover) {
        const modifiedExtras = optionalExtrasWithModifiedState.map((extra) => {
          if (extra.code === "GOLF_EQUIPMENT") {
            return golfCover;
          }
          return extra;
        });
        setOptionalExtrasWithModifiedState(modifiedExtras);
      }
      setIsGolfModification(false);
    } else if (priceResult) {
      setOptionalExtrasWithModifiedState(priceResult.optionalExtras);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceResult]);

  useEffect(() => {
    if (optionalExtrasWithModifiedState) {
      const optionalExtrasItems = optionalExtrasWithModifiedState.map(
        (extra: OptionalExtra) => {
          const cmsData = editPolicyHeaderMiddlePanelData.acoItems.find(
            (acoItem) => acoItem.acoId === extra.code
          );

          const extraData = extra.price[0]; // Use the first price option

          return {
            code: extra.code,
            title: cmsData?.acoTitle ?? extraData.name,
            description: cmsData?.description ?? `Cover for ${extraData.name}.`,
            sellPrice: extraData.sellPrice,
            pclPrice: extraData.pclPrice,
            pclTotalCost: extraData.pclTotalCost,
            selected: extraData.selected,
            state: extraData.state,
            actionLabel: extraData.selected
              ? editPolicyHeaderMiddlePanelData.addedButtonLabel
              : editPolicyHeaderMiddlePanelData.addButtonLabel,
            isGolfEquipment: extra.code === "GOLF_EQUIPMENT",
            golfSelectKey: extra.price[0].detail?.value,
            drawerContent: cmsData?.drawerContent ?? cmsData?.description ?? "",
          };
        }
      );

      const optionalExtrasAdditionalItems = optionalExtrasItems?.filter(
        (extra) =>
          (["ENABLED", "INCLUDED"].some((state) =>
            extra.state.includes(state)
          ) &&
            extra.selected) ||
          (extra.state.includes("ENABLED") && !extra.selected)
      );

      setOptionalExtrasDisplayItems(optionalExtrasAdditionalItems);
    }
  }, [editPolicyHeaderMiddlePanelData, optionalExtrasWithModifiedState]);

  const handleAddOptionalExtrasItem = useCallback(
    (code: string, value?: string) => {
      if (code === "GOLF_EQUIPMENT" && value) {
        setIsGolfModification(true);
        onChangeGolfCoverTravelersCount(value, code);
      } else {
        handleOptionalExtrasStateChange(code, true);
      }
    },

    [
      handleOptionalExtrasStateChange,
      onChangeGolfCoverTravelersCount,
      setIsGolfModification,
    ]
  );

  const handleRemoveOptionalExtrasItem = useCallback(
    (code: string) => {
      if (code === "GOLF_EQUIPMENT") {
        setIsGolfModification(true);
        onChangeGolfCoverTravelersCount("0", code);
      } else {
        handleOptionalExtrasStateChange(code, false);
      }
    },
    [
      setIsGolfModification,
      onChangeGolfCoverTravelersCount,
      handleOptionalExtrasStateChange,
    ]
  );

  const getFullPrice = (
    extraCode: string,
    sellPrice?: number,
    isSelected?: boolean
  ) => {
    if (extraCode === "GOLF_EQUIPMENT")
      return sellPrice && isSelected
        ? sellPrice.toFixed(2)
        : sellPrice?.toFixed(2) || GOLF_YEAR_PRICE[brand ?? "staysure"];

    if (sellPrice) return sellPrice.toFixed(2);
    return "N/A";
  };

  return (
    <div className="grid grid-cols-6 md:grid-cols-12  lg:bg-surface-neutral-option-1 rounded-2xl mt-spacing-l md:mt-spacing-xl lg:mt-spacing-xxl">
      <div className="col-span-12">
        <div
          className="md:max-w-[1199px]  min-w-[312px] mx-spacing-l md:mx-spacing-xxl lg:mx-auto my-0 md:w-auto relative bg-surface-neutral-option-1 
  md:p-spacing-xxl p-spacing-m rounded-radius-xxl"
        >
          <div
            className={` grid lg:grid grid-cols-1 lg:grid-cols-2 gap-y-spacing-0 md:gap-y-spacing-l lg:gap-x-spacing-5xl`}
          >
            {optionalExtrasDisplayItems?.map((extra) => (
              <Fragment key={extra.code}>
                <div>
                  <PolicyExtrasOption
                    key={extra.code}
                    title={extra.title}
                    fullPrice={`£${getFullPrice(
                      extra.code,
                      extra.sellPrice,
                      extra.selected
                    )}`}
                    monthlyPrice={extra.pclPrice ? `£${extra.pclPrice}` : ""}
                    totalPrice={
                      extra.pclTotalCost ? `£${extra.pclTotalCost}` : ""
                    }
                    description={extra.description}
                    state={extra.state}
                    actionLabel={extra.actionLabel}
                    action={handleAddOptionalExtrasItem}
                    code={extra.code}
                    isSelected={extra.selected}
                    moreInfoLabel={
                      editPolicyHeaderMiddlePanelData.moreInfoLabel
                    }
                    isGolfEquipment={extra.isGolfEquipment}
                    removeLabel={
                      editPolicyHeaderMiddlePanelData.removeButtonLabel
                    }
                    chooseLabel={editPolicyHeaderMiddlePanelData.chooseLabel}
                    removeAction={handleRemoveOptionalExtrasItem}
                    golfSelectKey={extra.golfSelectKey || "0"}
                    drawerCloseCta={
                      editPolicyHeaderMiddlePanelData.backToRenewalsLabel
                    }
                    drawerContent={extra.drawerContent}
                    tdeModalTitle={
                      editPolicyHeaderMiddlePanelData.tdeModalTitle
                    }
                    tdeModalCopy={editPolicyHeaderMiddlePanelData.tdeModalCopy}
                    tdeModalConfirmBtnLabel={
                      editPolicyHeaderMiddlePanelData.tdeModalConfirmBtnLabel
                    }
                    tdeModalCancelBtnLabel={
                      editPolicyHeaderMiddlePanelData.tdeModalCancelBtnLabel
                    }
                  />
                  <Divider
                    className="md:hidden !my-spacing-m"
                    color="#DCDCDC"
                  />
                </div>
              </Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default withBrand(RenewalEditPolicyExtrasMiddlePanel);
